import React, { useEffect, useRef, useState } from 'react';
import { PaymentRequestButtonElement, useStripe } from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';

// The wallet sheet (Apple Pay / Google Pay) must be completed within ~30s of the
// 'paymentmethod' event or the browser dismisses it as failed. We budget well
// under that for the backend round-trip, because this Django app shares its
// worker pool with synchronous LLM calls and can stall.
const COMPLETE_DEADLINE_MS = 30000;
const INTENT_BUDGET_MS = 15000;

/**
 * Apple Pay / Google Pay button via Stripe's Payment Request API.
 *
 * Renders NOTHING when no wallet is available (non-Safari browser, no card in
 * Wallet, domain not registered with Stripe), the card form below is always the
 * fallback, so this component is safe to mount unconditionally.
 *
 * Safety properties (each one closes a real double-charge / lockout hole):
 *  - The PaymentIntent is created only when the user confirms in the sheet, never
 *    on mount, so revisiting the pay screen can't reset a paid session.
 *  - `disabled` hides the button while the card path is charging, so a user can't
 *    fire both payment paths concurrently and get charged twice.
 *  - The 30s sheet deadline is enforced: if the backend is slow we abort BEFORE
 *    confirming, rather than charging a card against a sheet that already failed.
 *  - Every ev.complete() is wrapped, a throw from an expired sheet must never
 *    skip the post-payment unlock.
 *
 * Props:
 *   amount       int, cents. Must equal the backend price (fetched, not hardcoded).
 *   label        string, line item shown on the sheet.
 *   createIntent async () => ({ clientSecret, id, amount, already_paid? })
 *   onPaid       async (intentId) => post-payment unlock (status POST, navigation).
 *   disabled     bool, true while the card path is in flight or already paid.
 */
export default function WalletPayButton({ amount, label, createIntent, onPaid, disabled = false }) {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);

  // Refs so the handler always sees the latest props without re-creating the
  // PaymentRequest (Stripe forbids mutating it after canMakePayment).
  const cbRef = useRef({ createIntent, onPaid, amount, disabled });
  cbRef.current = { createIntent, onPaid, amount, disabled };

  useEffect(() => {
    if (!stripe) return;
    let cancelled = false;

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: { label, amount },
    });

    // Complete the sheet without ever throwing: once the 30s window has passed,
    // Apple Pay JS raises on complete(), and an unhandled throw here would skip
    // the unlock for a card we already charged.
    const safeComplete = (ev, status) => {
      try { ev.complete(status); } catch (_) { /* sheet already closed/expired */ }
    };

    pr.on('paymentmethod', async (ev) => {
      const startedAt = Date.now();
      const elapsed = () => Date.now() - startedAt;

      // Guard: the card path may have started charging between render and tap.
      if (cbRef.current.disabled) {
        safeComplete(ev, 'fail');
        toast.error('A payment is already in progress. Please wait.');
        return;
      }

      // 1. Create the intent, time-boxed so we never blow the sheet's deadline.
      let intent;
      try {
        intent = await Promise.race([
          cbRef.current.createIntent(),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('timeout')), INTENT_BUDGET_MS)),
        ]);
      } catch (e) {
        safeComplete(ev, 'fail');
        toast.error(
          e && e.message === 'timeout'
            ? 'Payment is taking too long to start. Please try the card form below.'
            : (e && e.message) || 'Could not start the payment. Please use the card form below.',
        );
        return;
      }

      // Session was already paid, unlock without charging again.
      if (intent && intent.already_paid) {
        safeComplete(ev, 'success');
        try {
          await cbRef.current.onPaid(intent.id);
        } catch (_) {
          toast.error('You have already paid. Please refresh the page to continue.');
        }
        return;
      }

      if (!intent || !intent.clientSecret) {
        safeComplete(ev, 'fail');
        toast.error('Payment service did not return a client secret. Please try again.');
        return;
      }

      // Price-drift guard: never charge an amount other than the one on the sheet.
      if (intent.amount && intent.amount !== cbRef.current.amount) {
        safeComplete(ev, 'fail');
        toast.error('The price has changed, please refresh the page and try again.');
        return;
      }

      // 2. If the deadline is already blown, ABORT BEFORE CHARGING. Confirming now
      //    would take real money against a sheet the browser has failed.
      if (elapsed() > COMPLETE_DEADLINE_MS - 5000) {
        safeComplete(ev, 'fail');
        toast.error('That took too long and the payment sheet expired. You have NOT been charged, please try the card form below.');
        return;
      }

      // 3. Confirm. handleActions:false so the sheet can close before any 3-D
      //    Secure challenge is presented.
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        intent.clientSecret,
        { payment_method: ev.paymentMethod.id },
        { handleActions: false },
      );
      if (error) {
        safeComplete(ev, 'fail');
        toast.error(error.message || 'Payment failed. Please use the card form below.');
        return;
      }
      safeComplete(ev, 'success');

      let final = paymentIntent;
      if (final && final.status === 'requires_action') {
        const next = await stripe.confirmCardPayment(intent.clientSecret);
        if (next.error) {
          toast.error(next.error.message || 'Payment authentication failed.');
          return;
        }
        final = next.paymentIntent;
      }
      if (!final || final.status !== 'succeeded') {
        toast.error('Payment not completed. Please try again.');
        return;
      }

      // 4. The card HAS been charged. Nothing past this point may tell the user
      //    the payment failed, or they will pay twice. onPaid retries internally
      //    and, if it still fails, leaves a recoverable intent id behind.
      try {
        await cbRef.current.onPaid(intent.id);
      } catch (_) {
        toast.error('Payment succeeded. We hit a problem unlocking your report, reload this page and it will resume automatically. Do NOT pay again.');
      }
    });

    pr.canMakePayment().then((result) => {
      if (result && !cancelled) setPaymentRequest(pr);
    });

    return () => { cancelled = true; };
    // amount/label are fixed for the lifetime of a pay screen; `disabled` is read
    // through cbRef so it never re-creates the PaymentRequest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe, amount, label]);

  // `amount` must be the real backend price. The Stripe PaymentRequest total is
  // IMMUTABLE once created, so if we rendered with a fallback and the true price
  // differed, the sheet would keep showing the stale amount while charging the
  // real one. Callers pass amount only once the price has loaded; guard anyway.
  if (!amount || !paymentRequest || disabled) return null;

  return (
    <div className="mb-4">
      <PaymentRequestButtonElement
        options={{ paymentRequest, style: { paymentRequestButton: { height: '44px' } } }}
      />
      <div className="flex items-center gap-3 mt-4">
        <span className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400">or pay with card</span>
        <span className="flex-1 h-px bg-gray-200" />
      </div>
    </div>
  );
}
